/** Single source of truth for Slovak okresy → kraj. */

/** [canonical okres, kraj] — all 79 districts. */
const DISTRICTS: ReadonlyArray<readonly [string, string]> = [
  ['Bratislava I', 'Bratislavský kraj'], ['Bratislava II', 'Bratislavský kraj'],
  ['Bratislava III', 'Bratislavský kraj'], ['Bratislava IV', 'Bratislavský kraj'],
  ['Bratislava V', 'Bratislavský kraj'], ['Malacky', 'Bratislavský kraj'],
  ['Pezinok', 'Bratislavský kraj'], ['Senec', 'Bratislavský kraj'],
  ['Dunajská Streda', 'Trnavský kraj'], ['Galanta', 'Trnavský kraj'],
  ['Hlohovec', 'Trnavský kraj'], ['Piešťany', 'Trnavský kraj'],
  ['Senica', 'Trnavský kraj'], ['Skalica', 'Trnavský kraj'], ['Trnava', 'Trnavský kraj'],
  ['Bánovce nad Bebravou', 'Trenčiansky kraj'], ['Ilava', 'Trenčiansky kraj'],
  ['Myjava', 'Trenčiansky kraj'], ['Nové Mesto nad Váhom', 'Trenčiansky kraj'],
  ['Partizánske', 'Trenčiansky kraj'], ['Považská Bystrica', 'Trenčiansky kraj'],
  ['Prievidza', 'Trenčiansky kraj'], ['Púchov', 'Trenčiansky kraj'],
  ['Trenčín', 'Trenčiansky kraj'],
  ['Komárno', 'Nitriansky kraj'], ['Levice', 'Nitriansky kraj'],
  ['Nitra', 'Nitriansky kraj'], ['Nové Zámky', 'Nitriansky kraj'],
  ['Šaľa', 'Nitriansky kraj'], ['Topoľčany', 'Nitriansky kraj'],
  ['Zlaté Moravce', 'Nitriansky kraj'],
  ['Bytča', 'Žilinský kraj'], ['Čadca', 'Žilinský kraj'],
  ['Dolný Kubín', 'Žilinský kraj'], ['Kysucké Nové Mesto', 'Žilinský kraj'],
  ['Liptovský Mikuláš', 'Žilinský kraj'], ['Martin', 'Žilinský kraj'],
  ['Námestovo', 'Žilinský kraj'], ['Ružomberok', 'Žilinský kraj'],
  ['Turčianske Teplice', 'Žilinský kraj'], ['Tvrdošín', 'Žilinský kraj'],
  ['Žilina', 'Žilinský kraj'],
  ['Banská Bystrica', 'Banskobystrický kraj'], ['Banská Štiavnica', 'Banskobystrický kraj'],
  ['Brezno', 'Banskobystrický kraj'], ['Detva', 'Banskobystrický kraj'],
  ['Krupina', 'Banskobystrický kraj'], ['Lučenec', 'Banskobystrický kraj'],
  ['Poltár', 'Banskobystrický kraj'], ['Revúca', 'Banskobystrický kraj'],
  ['Rimavská Sobota', 'Banskobystrický kraj'], ['Veľký Krtíš', 'Banskobystrický kraj'],
  ['Zvolen', 'Banskobystrický kraj'], ['Žarnovica', 'Banskobystrický kraj'],
  ['Žiar nad Hronom', 'Banskobystrický kraj'],
  ['Bardejov', 'Prešovský kraj'], ['Humenné', 'Prešovský kraj'],
  ['Kežmarok', 'Prešovský kraj'], ['Levoča', 'Prešovský kraj'],
  ['Medzilaborce', 'Prešovský kraj'], ['Poprad', 'Prešovský kraj'],
  ['Prešov', 'Prešovský kraj'], ['Sabinov', 'Prešovský kraj'],
  ['Snina', 'Prešovský kraj'], ['Stará Ľubovňa', 'Prešovský kraj'],
  ['Stropkov', 'Prešovský kraj'], ['Svidník', 'Prešovský kraj'],
  ['Vranov nad Topľou', 'Prešovský kraj'],
  ['Gelnica', 'Košický kraj'], ['Košice I', 'Košický kraj'],
  ['Košice II', 'Košický kraj'], ['Košice III', 'Košický kraj'],
  ['Košice IV', 'Košický kraj'], ['Košice-okolie', 'Košický kraj'],
  ['Michalovce', 'Košický kraj'], ['Rožňava', 'Košický kraj'],
  ['Sobrance', 'Košický kraj'], ['Spišská Nová Ves', 'Košický kraj'],
  ['Trebišov', 'Košický kraj'],
]

export const SLOVAK_DISTRICTS: ReadonlyArray<string> = DISTRICTS.map(([d]) => d)

/** Diacritic + case fold for tolerant matching. */
function fold(s: string): string {
  return s
    .normalize('NFD')
    .replace(/\p{Mn}/gu, '') // strip combining diacritical marks (NFD)
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

const REGION_BY_FOLDED_DISTRICT = new Map(DISTRICTS.map(([d, r]) => [fold(d), r]))
const CANONICAL_BY_FOLDED = new Map(DISTRICTS.map(([d]) => [fold(d), d]))

/** "Okres Martin" / "MARTIN" / null → canonical okres or null. */
export function normalizeDistrict(raw: string | null | undefined): string | null {
  if (!raw) return null
  const cleaned = raw.replace(/^okres\s+/i, '')
  return CANONICAL_BY_FOLDED.get(fold(cleaned)) ?? null
}

/** Canonical or messy okres → kraj label. */
export function resolveRegionFromDistrict(district: string | null | undefined): string | null {
  if (!district) return null
  return REGION_BY_FOLDED_DISTRICT.get(fold(district.replace(/^okres\s+/i, ''))) ?? null
}

// Veľké mestá → kraj (pre prípad, keď máme len mesto, nie okres)
const CITY_TO_REGION: Record<string, string> = {
  'bratislava': 'Bratislavský kraj',
  'trnava': 'Trnavský kraj',
  'trenčín': 'Trenčiansky kraj',
  'nitra': 'Nitriansky kraj',
  'žilina': 'Žilinský kraj',
  'banská bystrica': 'Banskobystrický kraj',
  'prešov': 'Prešovský kraj',
  'košice': 'Košický kraj',
  'martin': 'Žilinský kraj',
  'poprad': 'Prešovský kraj',
  'zvolen': 'Banskobystrický kraj',
  'piešťany': 'Trnavský kraj',
  'považská bystrica': 'Trenčiansky kraj',
  'prievidza': 'Trenčiansky kraj',
  'komárno': 'Nitriansky kraj',
  'levice': 'Nitriansky kraj',
  'nové zámky': 'Nitriansky kraj',
  'michalovce': 'Košický kraj',
  'humenné': 'Prešovský kraj',
  'ružomberok': 'Žilinský kraj',
  'liptovský mikuláš': 'Žilinský kraj',
  'lučenec': 'Banskobystrický kraj',
  'rimavská sobota': 'Banskobystrický kraj',
  'dunajská streda': 'Trnavský kraj',
  'galanta': 'Trnavský kraj',
  'malacky': 'Bratislavský kraj',
  'pezinok': 'Bratislavský kraj',
  'senec': 'Bratislavský kraj',
  'senica': 'Trnavský kraj',
  'skalica': 'Trnavský kraj',
  'hlohovec': 'Trnavský kraj',
  'šaľa': 'Nitriansky kraj',
  'topoľčany': 'Nitriansky kraj',
  'partizánske': 'Trenčiansky kraj',
  'bánovce nad bebravou': 'Trenčiansky kraj',
  'púchov': 'Trenčiansky kraj',
  'ilava': 'Trenčiansky kraj',
  'myjava': 'Trenčiansky kraj',
  'nové mesto nad váhom': 'Trenčiansky kraj',
  'čadca': 'Žilinský kraj',
  'dolný kubín': 'Žilinský kraj',
  'námestovo': 'Žilinský kraj',
  'tvrdošín': 'Žilinský kraj',
  'brezno': 'Banskobystrický kraj',
  'detva': 'Banskobystrický kraj',
  'žiar nad hronom': 'Banskobystrický kraj',
  'kežmarok': 'Prešovský kraj',
  'bardejov': 'Prešovský kraj',
  'levoča': 'Prešovský kraj',
  'snina': 'Prešovský kraj',
  'stará ľubovňa': 'Prešovský kraj',
  'stropkov': 'Prešovský kraj',
  'svidník': 'Prešovský kraj',
  'vranov nad topľou': 'Prešovský kraj',
  'spišská nová ves': 'Košický kraj',
  'rožňava': 'Košický kraj',
  'trebišov': 'Košický kraj',
  'gelnica': 'Košický kraj',
  'sobrance': 'Košický kraj',
  'sabinov': 'Prešovský kraj',
  'medzilaborce': 'Prešovský kraj',
  'krupina': 'Banskobystrický kraj',
  'poltár': 'Banskobystrický kraj',
  'revúca': 'Banskobystrický kraj',
  'veľký krtíš': 'Banskobystrický kraj',
  'žarnovica': 'Banskobystrický kraj',
  'banská štiavnica': 'Banskobystrický kraj',
  'zlaté moravce': 'Nitriansky kraj',
  'bytča': 'Žilinský kraj',
  'kysucké nové mesto': 'Žilinský kraj',
  'turčianske teplice': 'Žilinský kraj',
  'košice-okolie': 'Košický kraj',
  // Mestské časti Bratislavy
  'staré mesto': 'Bratislavský kraj',
  'ružinov': 'Bratislavský kraj',
  'nové mesto': 'Bratislavský kraj',
  'petržalka': 'Bratislavský kraj',
  'karlova ves': 'Bratislavský kraj',
  'dúbravka': 'Bratislavský kraj',
  'rača': 'Bratislavský kraj',
  'vajnory': 'Bratislavský kraj',
  'devín': 'Bratislavský kraj',
  'devínska nová ves': 'Bratislavský kraj',
  'záhorská bystrica': 'Bratislavský kraj',
  'lamač': 'Bratislavský kraj',
  'čunovo': 'Bratislavský kraj',
  'jarovce': 'Bratislavský kraj',
  'rusovce': 'Bratislavský kraj',
  'podunajské biskupice': 'Bratislavský kraj',
  'vrakuňa': 'Bratislavský kraj',
}

const REGION_BY_FOLDED_CITY = new Map(
  Object.entries(CITY_TO_REGION).map(([k, v]) => [fold(k), v]),
)

/** Resolve region from city name (exact, then bidirectional prefix match). Diacritic/case insensitive. */
export function resolveRegionFromCity(city: string | null | undefined): string | null {
  if (!city) return null
  const key = fold(city)
  const exact = REGION_BY_FOLDED_CITY.get(key)
  if (exact) return exact
  // Bidirectional prefix match handles e.g. "Bratislava - Staré Mesto" → "bratislava"
  for (const [cityKey, region] of REGION_BY_FOLDED_CITY) {
    if (key.startsWith(cityKey) || cityKey.startsWith(key)) return region
  }
  return null
}

/** Resolve region from okres first, then city. */
export function resolveRegion(
  district: string | null | undefined,
  city: string | null | undefined,
): string | null {
  return resolveRegionFromDistrict(district) ?? resolveRegionFromCity(city) ?? null
}
